import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from "@angular/core";
import { isPlatformBrowser, CommonModule } from "@angular/common";
import { Select, Store } from "@ngxs/store";
import { GetCountries } from "./shared/action/country.action";
import { ValidateToken } from "./core/auth/state/auth.action";
import { isTokenExpired, decodeToken } from "./core/auth/utils/token.utils";
import * as AOS from "aos";
import { datadogRum, Site } from "@datadog/browser-rum";
import { environment } from "src/environments/environment";
import { VERSION } from "src/environments/version";
import { NavigationEnd, Router, RouterModule } from "@angular/router";
import { AccountState } from "./core/account/state/account.state";
import { Observable, Subject, combineLatest, filter, takeUntil } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { PrivateProfileModalComponent } from "./shared/components/private-profile-modal/private-profile-modal.component";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: true,
  imports: [CommonModule, RouterModule]})
export class AppComponent implements OnInit, OnDestroy {
  @Select(AccountState.accountType) accountType$: Observable<string>;
  @Select(AccountState.streamer) streamer$: Observable<any>;

  private destroy$ = new Subject<void>();
  private modalShown = false;
  private overlayObserver: MutationObserver | null = null;
  private readonly bottomSheetBodyClass = "bottom-sheet-open";

  constructor(
    private _store: Store,
    private router: Router,
    private dialog: MatDialog,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    this._store.dispatch(new GetCountries());

    if (isPlatformBrowser(this.platformId)) {
      let accessToken =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");

      if (accessToken) {
        if (isTokenExpired(accessToken)) {
          localStorage.removeItem("accessToken");
          sessionStorage.removeItem("accessToken");
        } else {
          const decodedToken = decodeToken(accessToken);
          if (decodedToken) {
            this._store.dispatch(new ValidateToken(accessToken));
          } else {
            localStorage.removeItem("accessToken");
            sessionStorage.removeItem("accessToken");
          }
        }
      }

      AOS.init();

      if (environment.stage === "prod") {
        datadogRum.init({
          applicationId: environment.datadog.applicationId,
          clientToken: environment.datadog.clientToken,
          site: environment.datadog.site as Site,
          service: "streamer-web",
          env: environment.stage,
          version: VERSION.full,
          sessionSampleRate: 100,
          sessionReplaySampleRate: 100,
          startSessionReplayRecordingManually: true,
          trackResources: true,
          trackLongTasks: true,
          trackUserInteractions: true,
          enablePrivacyForActionName: true});
      }
    }
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.setupScrollResetOnNavigation();
    this.setupBottomSheetMobileMenuObserver();

    // Monitorar mudanças no estado do streamer para detectar login
    combineLatest([
      this.accountType$,
      this.streamer$
    ])
    .pipe(
      takeUntil(this.destroy$),
      filter(([accountType, streamer]) => {
        // Só verificar se for streamer e tiver dados carregados
        return accountType === 'streamer' && streamer != null && !this.modalShown;
      })
    )
    .subscribe(([_, streamer]) => {
      // Verificar se deve mostrar o modal (apenas após login)
      const shouldShowModal = sessionStorage.getItem('checkPrivateProfile');
      
      if (shouldShowModal === 'true') {
        // Remover a flag imediatamente
        sessionStorage.removeItem('checkPrivateProfile');
        
        // Se o perfil está privado, mostrar modal
        if (streamer.public === false) {
          this.modalShown = true;
          this.showPrivateProfileModal();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.overlayObserver?.disconnect();
    this.overlayObserver = null;
    if (isPlatformBrowser(this.platformId)) {
      document.body?.classList.remove(this.bottomSheetBodyClass);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private showPrivateProfileModal(): void {
    this.dialog.open(PrivateProfileModalComponent, {
      width: '650px',
      maxWidth: '90vw',
      disableClose: false,
      panelClass: 'private-profile-modal-container',
      backdropClass: 'private-profile-modal-backdrop'});
  }

  private setupBottomSheetMobileMenuObserver(): void {
    if (typeof document === "undefined" || typeof MutationObserver === "undefined") {
      return;
    }

    const syncBottomSheetBodyClass = () => {
      const hasOpenedBottomSheet = !!document.querySelector(
        ".cdk-overlay-container .mat-bottom-sheet-container, .cdk-overlay-container .mat-mdc-bottom-sheet-container",
      );

      document.body?.classList.toggle(this.bottomSheetBodyClass, hasOpenedBottomSheet);
    };

    syncBottomSheetBodyClass();

    this.overlayObserver?.disconnect();
    this.overlayObserver = new MutationObserver(() => {
      syncBottomSheetBodyClass();
    });

    this.overlayObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"]});
  }

  private setupScrollResetOnNavigation(): void {
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      )
      .subscribe((event) => {
        if (event.urlAfterRedirects.includes("#")) {
          return;
        }

        this.resetWindowScroll();
      });
  }

  private resetWindowScroll(): void {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    const scrollToTop = () => {
      const scrollingElement = document.scrollingElement as HTMLElement | null;
      scrollingElement?.scrollTo({ top: 0, left: 0, behavior: "auto" });
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    scrollToTop();
    requestAnimationFrame(scrollToTop);
  }
}
